// Dock section showing leads sent in the past 30 days — Slice B2.3 of
// docs/planning/activity_dock_workflow_facilitation.md. Collapsed by
// default with just a count, expands inline to render the list.
//
// This is intentionally NOT a self-perform card (there's no
// "re-send" action to do here) — it's a Click-to-navigate pattern in
// the workflow-facilitation taxonomy (§4.2). The action it
// facilitates is "I want to remember what I worked on this month" —
// the dock surfaces it so the user doesn't have to dig through
// Supabase or CRM tabs.
//
// Gated to sales roles only because:
//   1. The archived leads belong to the sales-role workflow.
//   2. Owner / admin / trainer don't need to see their reps' personal
//      sent-leads history in a small dock section — they have
//      dedicated dashboards.

import { useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useRecentSentLeads } from "../useRecentSentLeads";
import type { RecentSentLeadRow } from "@/lib/leads/fetchRecentSentLeads";

const SENT_LEADS_LOOKBACK_DAYS = 30;
const PREVIEW_CAP = 25;

function formatSentDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  // Compact format like "May 27" / "Jan 14, 2025" — fits in the
  // narrow side-anchored dock column.
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

function summarizeProduct(row: {
  summary: {
    product_line: string | null;
    product_subtype: string | null;
  };
}): string | null {
  const parts: string[] = [];
  if (row.summary.product_line) parts.push(row.summary.product_line);
  if (
    row.summary.product_subtype &&
    row.summary.product_subtype !== row.summary.product_line
  ) {
    parts.push(row.summary.product_subtype);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function ActivityDockSentLeadsSection() {
  const orgId = useScopedOrganizationId();
  const [expanded, setExpanded] = useState(false);
  const { state, refresh } = useRecentSentLeads({
    organizationId: orgId,
    days: SENT_LEADS_LOOKBACK_DAYS,
    enabled: Boolean(orgId),
  });

  if (!orgId) return null;
  if (state.status === "idle" || state.status === "loading") return null;
  if (state.status === "ready" && state.rows.length === 0) return null;

  const knownCount = state.status === "ready" ? state.rows.length : null;

  return (
    <section
      {...{ [FIREFLY_TARGET_ATTR]: "sent-leads-section" }}
      className="mt-3 rounded-2xl border border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-zinc-900"
      aria-label="Leads sent in the past 30 days"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={
          "flex w-full items-center justify-between gap-2 rounded-2xl px-3 py-2 text-left " +
          "transition-colors hover:bg-gray-50 dark:hover:bg-zinc-800/60"
        }
      >
        <span className="min-w-0">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Sent · past 30 days
          </span>
          <span className="mt-0.5 block text-sm font-medium text-gray-700 dark:text-gray-200">
            {knownCount === null
              ? "Tap to load history"
              : knownCount === 0
                ? "No leads sent in the past 30 days"
                : `${knownCount} lead${knownCount === 1 ? "" : "s"} sent`}
          </span>
        </span>
        <span
          aria-hidden
          className="shrink-0 text-gray-400 dark:text-gray-500"
        >
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-800">
          <SentLeadsBody state={state} onRetry={refresh} />
        </div>
      ) : null}
    </section>
  );
}

function SentLeadsBody({
  state,
  onRetry,
}: {
  state: ReturnType<typeof useRecentSentLeads>["state"];
  onRetry: () => void;
}) {
  // `idle` is the pre-effect initial render before the loading flip;
  // treat it the same as the no-cache loading branch below.
  if (state.status === "idle") {
    return (
      <p className="py-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
        Loading…
      </p>
    );
  }
  if (state.status === "loading" && (!state.rows || state.rows.length === 0)) {
    return (
      <p className="py-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
        Loading…
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <div className="space-y-1.5 py-1">
        <p className="text-[11px] text-rose-600 dark:text-rose-400">
          Couldn't load sent history: {state.error}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-gray-300 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-zinc-800"
        >
          Retry
        </button>
      </div>
    );
  }
  // For both `ready` and `loading-with-cache` we render the current
  // rows. `loading-with-cache` will silently refresh in the
  // background.
  const rows: RecentSentLeadRow[] | null =
    state.status === "ready" ? state.rows : state.rows;
  if (!rows || rows.length === 0) {
    return (
      <p className="py-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
        No leads sent in the past 30 days.
      </p>
    );
  }
  const preview = rows.slice(0, PREVIEW_CAP);
  const overflow = rows.length - preview.length;
  return (
    <ul className="space-y-1.5">
      {preview.map((row) => {
        const product = summarizeProduct(row);
        return (
          <li key={row.id} className="text-[11px]">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium text-gray-800 dark:text-gray-100">
                {row.summary.name || "Untitled lead"}
              </span>
              <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                {formatSentDate(row.sentAtIso)}
              </span>
            </div>
            {product ? (
              <div className="truncate text-gray-500 dark:text-gray-400">
                {product}
                {row.summary.customer_location
                  ? ` — ${row.summary.customer_location}`
                  : ""}
              </div>
            ) : null}
          </li>
        );
      })}
      {overflow > 0 ? (
        <li className="text-[11px] text-gray-500 dark:text-gray-400">
          …and {overflow} more
        </li>
      ) : null}
    </ul>
  );
}
